import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import {
  NotificationApplication,
  NotificationChannel,
  Subscription,
  Topic,
} from '@/core/notifications/model';
import { buildTagFilterFromValues } from '@/core/notifications/model/tag-filter-utils';
import { fieldSentenceLabel } from '@/core/notifications/model/tag-schema';
import { maybePromptPushEnable } from '@/core/notifications/components/push-enable-prompt';
import { Loading } from '@/shared/components/export';
import useSubscriptionWizardState, {
  scopeFieldStep,
  tagKeyOfScopeStep,
  WizardPreload,
  WizardStep,
} from './use-subscription-wizard-state';
import StepTopic from './steps/step-topic';
import StepScopeField from './steps/step-scope-field';
import StepScopeConfirm from './steps/step-scope-confirm';
import StepDelivery from './steps/step-delivery';

export interface SubscriptionWizardProps {
  topics: Topic[];
  applications: NotificationApplication[];
  channels: NotificationChannel[];
  existingSubscription?: Subscription | null;
  preload?: WizardPreload | null;
  onDone: (subscription: Subscription) => void;
  onCancel: () => void;
}

export interface SubscriptionWizardHandle {
  requestCancel: () => void;
}

function extractErrorMessage(error: any): string {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join('. ');
  if (typeof message === 'string') return message;
  return 'Validation failed';
}

function stepTitle(step: WizardStep, tagSchema: Topic['tagSchema']): ReactNode {
  if (step === 'topic') return 'Choose the topic and name your alarm';
  if (step === 'scope-confirm') return 'Review the scope of this alarm';
  if (step === 'delivery') return 'Choose where to deliver these notifications';
  const tagKey = tagKeyOfScopeStep(step);
  const fieldLabel = tagKey ? fieldSentenceLabel(tagSchema[tagKey], tagKey) : '';
  // The whole title is already bold, so bolding the field name wouldn't add
  // any contrast — underline it instead to call out which field this
  // question is actually about. Wizard-only treatment; elsewhere a field's
  // name is normalized for casing consistency (fieldHeaderLabel) rather than
  // visually emphasized.
  return (
    <>
      Receive every <span className="text-decoration-underline">{fieldLabel}</span>, or narrow it
      down?
    </>
  );
}

// The wizard itself, without any modal chrome, so it can be embedded (Settings,
// pipelines) or wrapped by subscription-wizard-modal.tsx.
function SubscriptionWizardBody(
  {
    topics,
    applications,
    channels,
    existingSubscription,
    preload,
    onDone,
    onCancel,
  }: SubscriptionWizardProps,
  ref: ForwardedRef<SubscriptionWizardHandle>,
) {
  const wizard = useSubscriptionWizardState({ topics, existingSubscription, preload });
  const [contactsLoaded, setContactsLoaded] = useState(false);

  const { setContacts } = wizard;
  useEffect(() => {
    notificationApi
      .getContacts()
      .then(setContacts)
      .catch(() => {})
      .finally(() => setContactsLoaded(true));
  }, [setContacts]);

  // A preload-started wizard never asked for topic/scope, so those steps only
  // exist once an edit link unlocks them — and 'topic' never does, since the
  // calling module decided it, not something this flow should ever revisit.
  const isPreload = !existingSubscription && !!preload;
  const scopeSteps: WizardStep[] = Object.keys(wizard.tagSchema).map(scopeFieldStep);
  const stepOrder: WizardStep[] = ['topic', ...scopeSteps, 'scope-confirm', 'delivery'];
  const reachableSteps = stepOrder.filter(
    step => step === 'delivery' || !isPreload || wizard.unlockedSteps.has(step),
  );
  const currentIndex = reachableSteps.indexOf(wizard.step);
  const currentTagKey = tagKeyOfScopeStep(wizard.step);
  // True while on a field's step reached via an "Edit" link from a summary
  // (scope-confirm, or the preload flow's fixed delivery-step summary) — see
  // goToFieldForEdit/finishEditingField in the wizard's state hook. Swaps the
  // usual Back/Next pair for a single "Done" that jumps straight back to that
  // summary, instead of stepping through every other field first.
  const isEditingSingleField = !!currentTagKey && !!wizard.editReturnStep;

  // Same gate subscription-form.tsx applies to its single Save button, split
  // across the steps that own each piece of it.
  const canLeaveTopicStep = !!wizard.name && !!wizard.topicKey;
  const canLeaveScopeConfirmStep =
    wizard.missingRequiredTagKeys.length === 0 && !wizard.hasInvalidTagValues;

  function canLeaveScopeFieldStep(tagKey: string): boolean {
    return (
      !wizard.missingRequiredTagKeys.includes(tagKey) && !(tagKey in wizard.invalidTagValuesMap)
    );
  }

  const canGoNext = (() => {
    if (wizard.step === 'topic') return canLeaveTopicStep;
    if (currentTagKey) return canLeaveScopeFieldStep(currentTagKey);
    return canLeaveScopeConfirmStep; // 'scope-confirm'
  })();
  const canSave = canLeaveTopicStep && canLeaveScopeConfirmStep && !wizard.saving;

  const buildChannelOverrides = (): Record<string, Record<string, unknown>> => {
    const channelOverrides: Record<string, Record<string, unknown>> = {};
    wizard.selectedChannels.forEach(channelKey => {
      const override = wizard.overrides[channelKey];
      if (override && Object.keys(override).length > 0) channelOverrides[channelKey] = override;
    });
    return channelOverrides;
  };

  const handleSave = async () => {
    wizard.setSaving(true);
    wizard.setErrorMessage(null);
    try {
      const common = {
        name: wizard.name,
        tagFilter: buildTagFilterFromValues(wizard.effectiveTagValues),
        defaultChannels: wizard.selectedChannels,
        channelOverrides: buildChannelOverrides(),
      };
      let saved: Subscription;
      if (existingSubscription) {
        // topic is immutable — omitted from the PATCH body
        saved = await notificationApi.updateSubscription(existingSubscription.id, common);
        toast.success('Alarm updated');
      } else {
        saved = await notificationApi.createSubscription({ ...common, topic: wizard.topicKey });
        toast.success('Alarm created');
      }
      if (wizard.selectedChannels.includes('push')) maybePromptPushEnable();
      onDone(saved);
    } catch (error: any) {
      const message = extractErrorMessage(error);
      wizard.setErrorMessage(message);
      toast.error(message);
    } finally {
      wizard.setSaving(false);
    }
  };

  const goBack = () => {
    const previous = reachableSteps[currentIndex - 1];
    if (previous) wizard.setStep(previous);
  };

  const goNext = () => {
    const next = reachableSteps[currentIndex + 1];
    if (next) wizard.setStep(next);
  };

  const requestCancel = () => {
    if (isEditingSingleField) wizard.finishEditingField();
    else onCancel();
  };
  useImperativeHandle(ref, () => ({ requestCancel }));

  return (
    <div>
      <div className="fw-bold mb-3" style={{ fontSize: 18 }}>
        {stepTitle(wizard.step, wizard.tagSchema)}
      </div>

      {wizard.step === 'topic' && (
        <StepTopic
          topics={topics}
          applications={applications}
          topicKey={wizard.topicKey}
          name={wizard.name}
          isEdit={wizard.isEdit}
          onSelectTopic={wizard.selectTopic}
          onNameChange={wizard.setName}
        />
      )}

      {currentTagKey && wizard.tagSchema[currentTagKey] && (
        <StepScopeField
          tagKey={currentTagKey}
          field={wizard.tagSchema[currentTagKey]}
          mode={wizard.scopeModeByField[currentTagKey] ?? 'all'}
          selectedValues={wizard.tagValues[currentTagKey] ?? []}
          onModeChange={mode => wizard.setScopeModeForField(currentTagKey, mode)}
          onToggleLeaf={leafKey => wizard.toggleTagLeaf(currentTagKey, leafKey)}
          onToggleGroup={(leafKeys, checked) =>
            wizard.setTagGroupLeaves(currentTagKey, leafKeys, checked)
          }
          onFreeText={raw => wizard.setTagFreeText(currentTagKey, raw)}
        />
      )}

      {wizard.step === 'scope-confirm' && (
        <StepScopeConfirm
          topic={wizard.selectedTopic}
          tagValues={wizard.effectiveTagValues}
          scopeModeByField={wizard.scopeModeByField}
          onEditField={tagKey => wizard.goToFieldForEdit(tagKey, 'scope-confirm')}
        />
      )}

      {wizard.step === 'delivery' &&
        (contactsLoaded ? (
          <StepDelivery
            channels={channels}
            topic={wizard.selectedTopic}
            isPreload={isPreload}
            name={wizard.name}
            tagValues={wizard.effectiveTagValues}
            scopeModeByField={wizard.scopeModeByField}
            missingRequiredTagKeys={wizard.missingRequiredTagKeys}
            invalidFieldKeys={Object.keys(wizard.invalidTagValuesMap)}
            selectedChannels={wizard.selectedChannels}
            overrides={wizard.overrides}
            contacts={wizard.contacts}
            onNameChange={wizard.setName}
            onToggleChannel={wizard.toggleChannel}
            onOverrideChange={wizard.setOverride}
            onContactAdded={(channelKey, contacts, addedContact) => {
              // `contacts` is omitted when the post-add refresh GET failed — keep
              // the wizard's existing contacts list rather than overwriting it
              // with an empty one.
              if (contacts) wizard.setContacts(contacts);
              // ChannelPicker only offers inline add for channels that don't
              // require verification, so `addedContact` is already verified —
              // assign it as this subscription's contact right away, and
              // select the channel if it wasn't already.
              wizard.setOverride(channelKey, addedContact.config);
              if (!wizard.selectedChannels.includes(channelKey)) {
                wizard.toggleChannel(channelKey);
              }
            }}
            onEditField={tagKey => wizard.goToFieldForEdit(tagKey, 'delivery')}
          />
        ) : (
          <Loading type="bar" />
        ))}

      {wizard.errorMessage && (
        <div
          className="px-3 py-2 rounded mb-3"
          style={{
            background: 'rgba(var(--bs-danger-rgb), 0.15)',
            border: '1px solid var(--bs-danger)',
            color: 'var(--bs-danger)',
            fontSize: 13,
          }}
        >
          {wizard.errorMessage}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 mt-3">
        {/* No Back in preload mode at all: its one non-edit step (delivery) has
            nothing before it, and once a field's been edited once,
            unlockedSteps keeps that field "reachable" forever, which would
            otherwise make currentIndex > 0 even while sitting on delivery. */}
        {!isPreload && !isEditingSingleField && currentIndex > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={goBack}
            disabled={wizard.saving}
          >
            Back
          </button>
        )}
        {isEditingSingleField ? (
          <button
            type="button"
            className="btn btn-sm btn-tertiary text-base-light"
            onClick={wizard.finishEditingField}
            disabled={!canGoNext}
          >
            Done
          </button>
        ) : wizard.step === 'delivery' ? (
          <button
            type="button"
            className="btn btn-sm btn-tertiary text-base-light"
            onClick={handleSave}
            disabled={!canSave}
          >
            {wizard.saving ? 'Saving…' : 'Save'}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-tertiary text-base-light"
            onClick={goNext}
            disabled={!canGoNext}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}

export default forwardRef(SubscriptionWizardBody);
